import React, { useEffect, useRef, useState } from "react"
import { usePage } from "@inertiajs/react"
import { Loader2, Mic, Square } from "lucide-react"
import voiceNotesApi from "@/api/Organizations/VoiceNotesApi"

const WAVEFORM_LEVELS = [
  0.25, 0.4, 0.55, 0.7, 0.45, 0.8, 0.6, 0.35, 0.9,
  0.5, 0.75, 0.42, 0.85, 0.58, 0.32, 0.68, 0.48, 0.78,
]

const formatDuration = (seconds) => {
  const minutes = Math.floor(seconds / 60)
  const remainder = seconds % 60

  return `${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`
}

const getCsrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content || ""

const getSupportedMimeType = () => {
  const types = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/ogg",
  ]

  return types.find((type) => MediaRecorder.isTypeSupported(type)) || ""
}

export default function VoiceNoteWidget({
  onRecordingCreated,
  onTranscript,
  createHighlight = true,
  compact = false,
  variant = "full",
  onActiveChange,
}) {
  const [status, setStatus] = useState("idle")
  const [errorMessage, setErrorMessage] = useState("")
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [volume, setVolume] = useState(0)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const mediaRecorderRef = useRef(null)
  const streamRef = useRef(null)
  const chunksRef = useRef([])
  const audioContextRef = useRef(null)
  const analyserFrameRef = useRef(null)
  const analyserIntervalRef = useRef(null)

  const { organization } = usePage().props.auth
  const isRecording = status === "recording"
  const isProcessing = status === "processing"
  const hasError = status === "error"
  const isActive = isRecording || isProcessing || hasError

  useEffect(() => {
    return () => {
      stopAnalyser()
      stopStream()
    }
  }, [])

  useEffect(() => {
    if (!isRecording) {
      setElapsedSeconds(0)
      return
    }

    const intervalId = window.setInterval(() => {
      setElapsedSeconds((current) => current + 1)
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [isRecording])

  useEffect(() => {
    onActiveChange?.(isActive)
  }, [isActive, onActiveChange])

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }

  const stopAnalyser = () => {
    if (analyserFrameRef.current) {
      window.cancelAnimationFrame(analyserFrameRef.current)
      analyserFrameRef.current = null
    }

    if (analyserIntervalRef.current) {
      window.clearInterval(analyserIntervalRef.current)
      analyserIntervalRef.current = null
    }

    audioContextRef.current?.close().catch(() => {})
    audioContextRef.current = null
    setIsSpeaking(false)
    setVolume(0)
  }

  const startAnalyser = (stream) => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) return

    const audioContext = new AudioContextClass()
    const analyser = audioContext.createAnalyser()
    const source = audioContext.createMediaStreamSource(stream)
    const data = new Uint8Array(analyser.fftSize)

    source.connect(analyser)
    audioContextRef.current = audioContext

    analyserIntervalRef.current = window.setInterval(() => {
      analyser.getByteTimeDomainData(data)

      let sum = 0
      for (let i = 0; i < data.length; i += 1) {
        const centered = (data[i] - 128) / 128
        sum += centered * centered
      }

      const rms = Math.sqrt(sum / data.length)
      const nextVolume = Math.min(1, rms * 8)
      setVolume(nextVolume)
      setIsSpeaking(nextVolume > 0.04)
    }, 80)
  }

  const uploadRecording = async (blob) => {
    const formData = new FormData()
    const extension = blob.type.includes("ogg") ? "ogg" : "webm"
    const csrfToken = getCsrfToken()

    formData.append("audio", blob, `voice-note.${extension}`)
    if (csrfToken) {
      formData.append("authenticity_token", csrfToken)
    }

    const endpoint = createHighlight ? voiceNotesApi.create : voiceNotesApi.transcribe
    const response = await fetch(endpoint.path({ org_nano_id: organization.nano_id }), {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
      body: formData,
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || "Could not transcribe audio.")
    }

    return response.json().catch(() => ({}))
  }

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      showError("Voice recording is not supported in this browser.")
      return
    }

    try {
      setErrorMessage("")
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = getSupportedMimeType()
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)

      chunksRef.current = []
      streamRef.current = stream
      mediaRecorderRef.current = recorder

      recorder.ondataavailable = (event) => {
        if (event.data?.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      recorder.onstop = async () => {
        stopAnalyser()
        stopStream()
        setStatus("processing")

        try {
          const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" })
          const data = await uploadRecording(blob)

          if (createHighlight) {
            onRecordingCreated?.()
          } else {
            const transcript = data.transcript?.trim()
            if (!transcript) {
              throw new Error("Transcript was empty.")
            }

            onTranscript?.(transcript)
          }

          setStatus("idle")
        } catch (error) {
          showError(error.message || "Could not transcribe audio.")
        } finally {
          chunksRef.current = []
          mediaRecorderRef.current = null
        }
      }

      recorder.start()
      startAnalyser(stream)
      setStatus("recording")
      setElapsedSeconds(0)
    } catch {
      stopAnalyser()
      stopStream()
      showError("Could not access your microphone.")
    }
  }

  const showError = (message) => {
    setErrorMessage(message)
    setStatus("error")
    window.setTimeout(() => {
      setStatus("idle")
    }, 2500)
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop()
    }
  }

  const toggleCall = async () => {
    if (isRecording) {
      stopRecording()
      return
    }

    if (!isProcessing) {
      await startRecording()
    }
  }

  const getBarHeight = (baseLevel) => {
    if (isProcessing) return 35 + baseLevel * 30
    if (!isRecording) return 20 + baseLevel * 12

    const activityBoost = isSpeaking ? Math.max(volume, 0.15) * 34 : 8
    return 24 + baseLevel * 28 + activityBoost
  }

  const controlWidthClass = isRecording || isProcessing ? "w-[250px]" : "w-[220px]"

  if (compact || variant === "toolbar") {
    if (variant === "toolbar" && isActive) {
      if (isProcessing) {
        return (
          <div
            className="flex h-7 w-7 animate-in fade-in duration-300 items-center justify-center text-muted-foreground"
            title="Transcribing voice note"
            aria-label="Transcribing voice note"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )
      }

      return (
        <div
          className={`flex h-7 items-center gap-1 bg-background px-1.5 shadow-sm transition-[box-shadow,width] duration-300 ${
            isRecording
              ? "shadow-[0_8px_18px_rgba(0,0,0,0.08)]"
              : hasError
                ? "shadow-[0_8px_18px_rgba(0,0,0,0.08)]"
                : ""
          }`}
          title={errorMessage || undefined}
        >
          <div
            className="flex h-5 w-5 shrink-0 items-center justify-center"
            aria-hidden="true"
          >
            {isProcessing ? (
              <Loader2 className="h-3 w-3 animate-spin text-amber-600" />
            ) : (
              <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-destructive shadow-[0_0_0_3px_rgba(239,68,68,0.12)]" />
            )}
          </div>

          <div className="w-9 shrink-0 text-[11px] font-medium tabular-nums text-muted-foreground">
            {formatDuration(elapsedSeconds)}
          </div>

          <button
            type="button"
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-sm transition-colors duration-200 ${
              isProcessing
                ? "text-amber-600"
              : isRecording
                  ? "text-destructive hover:bg-destructive/10"
                  : hasError
                    ? "text-destructive hover:bg-destructive/10"
                    : "text-primary hover:bg-primary/10"
            }`}
            onClick={toggleCall}
            disabled={isProcessing}
            aria-label={isRecording ? "Stop voice mode" : "Start voice mode"}
          >
            {isProcessing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : isRecording ? (
              <span className="h-3 w-3 rounded-[2px] bg-destructive" />
            ) : (
              <Mic className="h-3 w-3" />
            )}
          </button>
          {errorMessage && <span className="sr-only" role="alert">{errorMessage}</span>}
        </div>
      )
    }

    return (
      <>
        <button
          type="button"
          className={`flex h-7 w-7 items-center justify-center rounded-sm transition-colors ${
            isProcessing
              ? "text-amber-600"
              : isRecording
                ? "text-destructive hover:text-destructive/90"
                : hasError
                  ? "text-destructive hover:text-destructive/90"
                  : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={toggleCall}
          disabled={isProcessing}
          title={errorMessage || (isRecording ? "Stop voice mode" : "Voice mode")}
          aria-label={isRecording ? "Stop voice mode" : "Start voice mode"}
        >
          {isProcessing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isRecording ? (
            <Square className="h-3.5 w-3.5 fill-current" />
          ) : (
            <Mic className="h-4 w-4" />
          )}
        </button>
        {errorMessage && <span className="sr-only" role="alert">{errorMessage}</span>}
      </>
    )
  }

  return (
    <div className="flex h-full items-center justify-center">
      <div
        className={`flex h-9 items-center gap-2 overflow-hidden rounded-full border bg-background px-2.5 shadow-sm transition-[width,border-color,box-shadow] duration-300 ${
          isRecording
            ? "border-primary/20 shadow-[0_12px_28px_rgba(0,0,0,0.12)]"
            : hasError
              ? "border-destructive/40"
            : "border-border"
        } ${controlWidthClass}`}
        title={errorMessage || undefined}
      >
        <div className="w-10 shrink-0 text-xs font-medium tabular-nums text-muted-foreground">
          {formatDuration(elapsedSeconds)}
        </div>

        <div className="grid h-7 flex-1 items-center justify-items-center gap-1 [grid-template-columns:repeat(18,minmax(0,1fr))]">
          {WAVEFORM_LEVELS.map((level, index) => (
            <div
              key={`wave-${index}`}
              className={`w-0.5 rounded-full bg-muted-foreground/60 transition-all duration-200 ${
                isProcessing ? "animate-pulse" : ""
              } ${isRecording ? "opacity-100" : "opacity-45"}`}
              style={{ height: `${getBarHeight(level)}%` }}
            />
          ))}
        </div>

        <button
          type="button"
          className={`flex size-7 shrink-0 items-center justify-center rounded-full text-white transition-all duration-200 ${
            isProcessing
              ? "bg-amber-500"
              : isRecording
                ? "bg-destructive hover:bg-destructive/90"
                : hasError
                  ? "bg-destructive hover:bg-destructive/90"
                : "bg-primary hover:bg-primary/90"
          }`}
          onClick={toggleCall}
          disabled={isProcessing}
          aria-label={isRecording ? "End voice interview" : "Start voice interview"}
        >
          {isProcessing ? (
            <Loader2 className="size-4 animate-spin" />
          ) : isRecording ? (
            <Square className="size-3.5 fill-current" />
          ) : (
            <Mic className="size-4" />
          )}
        </button>
        {errorMessage && <span className="sr-only" role="alert">{errorMessage}</span>}
      </div>
    </div>
  )
}
